import { useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@bosch/react-frok'

import svgCameraImageFunc from '@/assets/svgImageCameraFunc.svg'
import svgImageMicFunc from '@/assets/svgImageMicFunc.svg'

import './FeatureOnboardingPopover.scss'

type OnboardingStep = 'camera' | 'mic'

type FeatureOnboardingPopoverProps = {
    anchorEl: HTMLElement | null
    step: OnboardingStep
    onGotIt: () => void
    onClose: () => void
    onOpenManual: () => void
}

const GAP = 16
const MOBILE_BREAKPOINT = 767
const MOBILE_SIDE_GUTTER = 8

type PopoverPos = {
    bottom: number
    left: number
    width: number
    arrowLeft: number
}

function FeatureOnboardingPopover({
    anchorEl,
    step,
    onGotIt,
    onClose,
    onOpenManual,
}: FeatureOnboardingPopoverProps) {
    const { t } = useTranslation()
    const [pos, setPos] = useState<PopoverPos | null>(null)

    useLayoutEffect(() => {
        if (!anchorEl) return
        const container =
            anchorEl.closest('.input-area') ||
            anchorEl.closest('.answer-area-root') ||
            anchorEl.closest('.chat-root') ||
            document.body
        const update = () => {
            const anchorRect = anchorEl.getBoundingClientRect()
            const containerRect = (container as HTMLElement).getBoundingClientRect()
            const isMobile = window.innerWidth <= MOBILE_BREAKPOINT
            const sideInset = isMobile ? MOBILE_SIDE_GUTTER : 0
            const left = containerRect.left + sideInset
            const width = Math.max(0, containerRect.width - sideInset * 2)
            const bottom = Math.max(GAP, window.innerHeight - anchorRect.top + GAP)
            const anchorCenterX = anchorRect.left + anchorRect.width / 2
            const arrowLeft = Math.max(16, Math.min(width - 16, anchorCenterX - left))
            setPos({ bottom, left, width, arrowLeft })
        }
        update()
        window.addEventListener('resize', update)
        window.addEventListener('scroll', update, true)
        return () => {
            window.removeEventListener('resize', update)
            window.removeEventListener('scroll', update, true)
        }
    }, [anchorEl, step])

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [onClose])

    if (!anchorEl || !pos) return null

    const isCamera = step === 'camera'
    const title = isCamera
        ? t('chat:input:onboarding:camera:title', { defaultValue: 'Camera Functionality' })
        : t('chat:input:onboarding:mic:title', { defaultValue: 'Mic to Mouth' })
    const smallText = isCamera
        ? t('chat:input:onboarding:camera:eyebrow', { defaultValue: 'New Feature' })
        : t('chat:input:onboarding:mic:eyebrow', { defaultValue: 'Tip' })
    const description = isCamera
        ? t('chat:input:onboarding:camera:description', {
            defaultValue: 'One picture says more than a thousand words. Enrich your ticket with a photo.',
        })
        : t('chat:input:onboarding:mic:description', {
            defaultValue: 'Hold the mic close. This helps us hear you over the noise.',
        })
    const image = isCamera ? svgCameraImageFunc : svgImageMicFunc

    return createPortal(
        <div
            className={`feature-onboarding-popover -${step}`}
            role="dialog"
            aria-label={title}
            style={{
                position: 'fixed',
                bottom: pos.bottom,
                left: pos.left,
                width: pos.width,
            }}
        >
            <button
                type="button"
                className="feature-onboarding-close"
                aria-label={t('chat:input:onboarding:close', { defaultValue: 'Close' })}
                onClick={onClose}
            >
                <i className='a-icon a-button__icon boschicon-bosch-ic-close'></i>
            </button>
            <div className="feature-onboarding-header">
                <h6 className="feature-onboarding-small-text">{smallText}</h6>
                <h4 className="feature-onboarding-title">{title}</h4>
                <p className="feature-onboarding-description">{description}</p>
            </div>
            <div className="feature-onboarding-illustration">
                <img src={image} alt="" className="feature-onboarding-image" aria-hidden="true" />
            </div>
            <div className="feature-onboarding-actions">
                {isCamera && (
                    <Button
                        mode="secondary"
                        className="user-manual-button"
                        label={t('chat:input:onboarding:userManual', { defaultValue: 'User Manual' })}
                        onClick={onOpenManual}
                    />
                )}
                <Button
                    mode="primary"
                    className="got-it-button"
                    label={t('chat:input:onboarding:gotIt', { defaultValue: 'Got it' })}
                    onClick={onGotIt}
                />
            </div>
            <span
                className="feature-onboarding-arrow"
                aria-hidden="true"
                style={{ left: pos.arrowLeft }}
            />
        </div>,
        document.body,
    )
}

export default FeatureOnboardingPopover
